/** Protocol / Figma reference curves — progress toward target (0→100). */
export const CANONICAL_ON_YOUR_OWN = [0, 12, 28, 50, 72, 100] as const;
export const CANONICAL_WITH_ED = [0, 18, 42, 70, 100] as const;

const TARGET_PROGRESS_MIN_LAST = 95;
const FLAT_SPREAD_THRESHOLD = 5;

export type TargetProgressSeriesVariant = "on-your-own" | "with-ed";

const canonicalFor = (
  variant: TargetProgressSeriesVariant,
): readonly number[] =>
  variant === "with-ed" ? CANONICAL_WITH_ED : CANONICAL_ON_YOUR_OWN;

/** Resample a canonical curve to `length` points; last point is always 100. */
export const resampleCanonicalProgressSeries = (
  canonical: readonly number[],
  length: number,
): number[] => {
  if (length <= 0) {
    return [];
  }
  if (length === 1) {
    return [100];
  }
  if (length === canonical.length) {
    return [...canonical];
  }

  const result: number[] = [];
  for (let i = 0; i < length; i++) {
    const t = i / (length - 1);
    const pos = t * (canonical.length - 1);
    const lo = Math.floor(pos);
    const hi = Math.min(canonical.length - 1, lo + 1);
    const frac = pos - lo;
    const value = canonical[lo]! * (1 - frac) + canonical[hi]! * frac;
    result.push(Math.round(value));
  }
  result[result.length - 1] = 100;
  return result;
};

/**
 * Normalize backend chart series so the line reaches the target dashed line.
 * Does not depend on API always sending 0–100 target-progress values.
 */
export const normalizeTargetProgressSeries = (
  raw: number[],
  variant: TargetProgressSeriesVariant = "on-your-own",
): number[] => {
  const canonical = canonicalFor(variant);

  if (raw.length === 0) {
    return resampleCanonicalProgressSeries(canonical, 6);
  }

  const clamped = raw.map((value) => Math.max(0, Math.min(100, value)));
  const last = clamped[clamped.length - 1] ?? 0;
  const spread = Math.max(...clamped) - Math.min(...clamped);

  if (last >= TARGET_PROGRESS_MIN_LAST && spread >= FLAT_SPREAD_THRESHOLD) {
    return clamped;
  }

  if (spread < FLAT_SPREAD_THRESHOLD) {
    return resampleCanonicalProgressSeries(canonical, clamped.length);
  }

  const peak = Math.max(...clamped, 1);
  const scaled = clamped.map((value) => Math.min(100, (value / peak) * 100));
  scaled[scaled.length - 1] = 100;
  return scaled;
};
